/**
 * @fileoverview The closed directive vocabulary, as marked extensions.
 *
 * Syntax follows the `remark-directive` convention the format spec names:
 * `:::name[label]{attrs}` opens a block and a line of `:::` closes it, and
 * `:name[label]{attrs}` is inline (`kbd`, `abbr`, `mark`, `pageref`, `ref`).
 * See `architecture/page-format.md`.
 *
 * THE VOCABULARY IS CLOSED, AND THAT IS ENFORCED HERE RATHER THAN HOPED FOR.
 * A block whose name is not in `BLOCKS` is not a directive at all: the
 * tokenizer declines it and the text stays literal, exactly as a reader with no
 * directive support would show it. An attribute outside a directive's own
 * allow-list is ignored, and a value outside its menu falls back to the
 * default. There is no path from author text to an attribute value we did not
 * write ourselves.
 *
 * EVERY DIRECTIVE EMITS A FIXED TEMPLATE. Author text is only ever content
 * inside it, escaped or parsed as markdown, and the whole result still goes
 * through DOMPurify in `renderMarkdown` with no path around it. These templates
 * use nothing DOMPurify does not already allow, so the sanitiser is a second
 * wall rather than the only one.
 *
 * NOTHING HERE NEEDS A BROWSER. A diagram is emitted as its source in a code
 * block. Drawing it (`diagram.ts`) is switched off for now; see the diagram
 * branch below. Run
 * headless, a diagram reads as the source it was written in, which is the
 * fallback by construction rather than by a branch.
 */

import type { MarkedExtension, Tokens } from 'marked';

import { localRouteForNostrUri, pagePath } from '@/services/nostr/pageLinks';
import { RENDERABLE_PAGE_KINDS } from '@/services/pages/kinds';

/** Where a fleet reference can point, for the renderer's config. */
export interface RefUrlTemplates {
  /**
   * `https://…{id}…`, or empty. EMPTY BY DEFAULT, because neither the tasks
   * board nor Space threads has a per-item link yet (cloistr-orchestrator,
   * 2026-10-15). A template set later turns every existing reference into a
   * link with no change to a published page.
   */
  card: string;
  thread: string;
}

export const NO_REF_URLS: RefUrlTemplates = { card: '', thread: '' };

const SEVERITIES = ['note', 'tip', 'warning', 'caution', 'important'] as const;
type Severity = (typeof SEVERITIES)[number];
const SEVERITY_TITLE: Record<Severity, string> = {
  note: 'Note',
  tip: 'Tip',
  warning: 'Warning',
  caution: 'Caution',
  important: 'Important',
};

const BLOCKS = new Set(['callout', 'details', 'diagram', 'figure', 'aside', 'columns', 'column', 'card', 'deflist', 'footnotes', 'toc']);

/** Each attribute's menu; the first entry is the default. */
const MENUS = {
  figureWidth: ['normal', 'inset', 'wide', 'full'],
  figureAlign: ['center', 'left', 'right'],
  asideAlign: ['right', 'left'],
  columnsCount: ['2', '3'],
  columnsRatio: ['even', 'wide-left', 'wide-right'],
  cardEmphasis: ['normal', 'subtle', 'strong'],
  tocDepth: ['2', '3'],
} as const;

function pick(menu: readonly string[], value: string | undefined): string {
  return value !== undefined && menu.includes(value) ? value : menu[0]!;
}

/** `: ` at the start of a line opens a definition; any other line is a term. */
const DEFINITION = /^ {0,3}:[ \t]+(.*)$/;

/** A footnote's id: short, and safe to put in an element id as it stands. */
const FOOTNOTE_ID = /^[a-z0-9][a-z0-9-]{0,31}$/;
/** `id: text` opens a note inside :::footnotes; other lines continue it. */
const FOOTNOTE_LINE = /^ {0,3}([A-Za-z0-9][A-Za-z0-9-]{0,31}):[ \t]+(.*)$/;

/** Diagram languages we draw. One, and a second is a decision. */
const DIAGRAM_TYPES = new Set(['mermaid']);

/**
 * Nesting beyond this is not a document, it is an attempt to make the
 * tokenizer recurse. A deeper block stays literal.
 */
const MAX_DEPTH = 8;

/** `:::name[label]{attrs}` at the start of a line, and nothing after it. */
const OPEN = /^ {0,3}:::([a-z][a-z-]{0,31})(?:\[([^\]\n]{0,200})\])?(?:\{([^}\n]{0,300})\})?[ \t]*(?:\n|$)/;
const ANY_OPEN = /^ {0,3}:::[a-z]/;
const CLOSE = /^ {0,3}:::[ \t]*$/;
const FENCE = /^ {0,3}(`{3,}|~{3,})/;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

/**
 * `key=value key="two words"` to a map. Keys are lowercase words; values are
 * bare or double-quoted, and neither form can contain a brace or a newline,
 * because the opener's own pattern already excluded both.
 */
export function parseAttributes(raw: string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw) return out;
  const pair = /([a-z][a-z-]{0,31})=(?:"([^"]*)"|([^\s"]+))/g;
  for (let m = pair.exec(raw); m; m = pair.exec(raw)) {
    const key = m[1]!;
    // First wins, as in a theme: the meaning must not depend on which of two
    // spellings came last.
    if (!(key in out)) out[key] = m[2] ?? m[3] ?? '';
  }
  return out;
}

/**
 * The body of a block opened on the first line of `src`, up to its matching
 * `:::`, or to the end of the document if it is never closed (as
 * remark-directive does). Openers inside the body nest; fenced code is
 * skipped, so a `:::` line in a code sample does not close anything.
 */
function containerExtent(src: string, openerLength: number): { body: string; raw: string } | null {
  const lines = src.slice(openerLength).split('\n');
  let depth = 1;
  let fence: string | null = null;
  const body: string[] = [];
  let consumed = openerLength;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const isLast = i === lines.length - 1;
    consumed += line.length + (isLast ? 0 : 1);

    const fenceMatch = FENCE.exec(line);
    if (fence) {
      if (fenceMatch && fenceMatch[1]!.startsWith(fence)) fence = null;
    } else if (fenceMatch) {
      fence = fenceMatch[1]!;
    } else if (ANY_OPEN.test(line)) {
      depth++;
      if (depth > MAX_DEPTH) return null;
    } else if (CLOSE.test(line)) {
      depth--;
      if (depth === 0) return { body: body.join('\n'), raw: src.slice(0, consumed) };
    }
    body.push(line);
  }
  return { body: body.join('\n'), raw: src };
}

interface ContainerToken extends Tokens.Generic {
  type: 'pagesContainer';
  name: string;
  label: string;
  attrs: Record<string, string>;
  /** Raw body, kept for a diagram, whose body is never markdown. */
  body: string;
  tokens: Tokens.Generic[];
  /** A figure's caption, as inline markdown. */
  caption?: Tokens.Generic[];
  /** A definition list's entries, each definition as inline markdown. */
  entries?: { term: string; definitions: Tokens.Generic[][] }[];
  /** Footnotes, each as inline markdown. Numbered later, from the refs. */
  notes?: { id: string; tokens: Tokens.Generic[] }[];
}

interface RefToken extends Tokens.Generic {
  type: 'pagesRef';
  label: string;
  attrs: Record<string, string>;
}

/**
 * The small inline set: a key, an abbreviation, a highlight, a link to a page
 * by its coordinate, and a footnote ref. Each emits one fixed element.
 */
const INLINE = /^:(kbd|abbr|mark|pageref|footnote-ref)\[([^\]\n]{0,200})\](?:\{([^}\n]{0,300})\})?/;
const INLINE_START = /:(?:kbd|abbr|mark|pageref|footnote-ref)\[/;

interface InlineToken extends Tokens.Generic {
  type: 'pagesInline';
  name: string;
  label: string;
  attrs: Record<string, string>;
  tokens: Tokens.Generic[];
}

const REF = /^:ref\[([^\]\n]{0,200})\]\{([^}\n]{0,300})\}/;
const CARD_ID = /^\d{1,9}$/;
const THREAD_ID = /^[A-Za-z0-9._:-]{1,128}$/;
const COORDINATE = /^(\d{1,5}):([0-9a-f]{64}):(.{1,256})$/;

/** A configured template with the id put in it, or null if it is not usable. */
function fromTemplate(template: string, id: string): string | null {
  if (!template.startsWith('https://') || !template.includes('{id}')) return null;
  return template.replace('{id}', encodeURIComponent(id));
}

/** A page reference, by naddr or coordinate, to a local route. */
function pageRoute(value: string): string | null {
  if (value.startsWith('naddr1')) return localRouteForNostrUri(`nostr:${value}`, RENDERABLE_PAGE_KINDS);
  const m = COORDINATE.exec(value);
  if (!m) return null;
  const kind = Number(m[1]);
  if (!RENDERABLE_PAGE_KINDS.includes(kind)) return null;
  // The same encoder the naddr path ends in, so a coordinate and an naddr for
  // one page land on one route.
  return pagePath({ kind, pubkey: m[2]!, identifier: m[3]! });
}

/**
 * A fleet reference: a board card, a thread or a page, with a small label
 * saying which.
 *
 * Exactly one of `card`, `thread` or `page`. THE AUTHOR NEVER WRITES A URL.
 * Card and thread links come from the renderer's templates, and render as a
 * labelled non-link while there is no template, which is today. A page
 * reference goes to a local route, by the same by-coordinate rule as every
 * other page link.
 */
function renderRef(token: RefToken, urls: RefUrlTemplates): string {
  const label = escapeHtml(token.label.trim());
  const { card, thread, page } = token.attrs;
  const given = [card, thread, page].filter((v) => v !== undefined).length;

  let kind: string | null = null;
  let tag = '';
  let href: string | null = null;
  if (given === 1 && card !== undefined && CARD_ID.test(card)) {
    kind = 'card';
    tag = `card #${card}`;
    href = fromTemplate(urls.card, card);
  } else if (given === 1 && thread !== undefined && THREAD_ID.test(thread)) {
    kind = 'thread';
    tag = 'thread';
    href = fromTemplate(urls.thread, thread);
  } else if (given === 1 && page !== undefined) {
    const route = pageRoute(page);
    if (route) {
      kind = 'page';
      tag = 'page';
      href = route;
    }
  }

  // Not one of the three, or a value outside its grammar: the author's text
  // stays, and nothing about it looks like a reference that failed.
  if (!kind) return label;

  const inner =
    `<span class="pages-ref-kind">${escapeHtml(tag)}</span>` +
    (label ? `<span class="pages-ref-label">${label}</span>` : '');
  if (!href) return `<span class="pages-ref" data-ref="${kind}">${inner}</span>`;
  const local = href.startsWith('/') ? ' data-page-link="true"' : '';
  return `<a class="pages-ref" data-ref="${kind}" href="${escapeHtml(href)}"${local}>${inner}</a>`;
}

/** The vocabulary as one marked extension. */
export function directiveExtension(urls: RefUrlTemplates = NO_REF_URLS): MarkedExtension {
  return {
    renderer: {
      // An `<input>` is forbidden by the sanitiser, which is right for page
      // content and would silently strip every task-list box. A task's state
      // is shown, not edited, so a span carries it.
      checkbox({ checked }) {
        return `<span class="pages-task" data-checked="${checked ? 'true' : 'false'}" role="img" aria-label="${checked ? 'done' : 'not done'}"></span> `;
      },
    },
    extensions: [
      {
        name: 'pagesContainer',
        level: 'block',
        start(src: string) {
          const m = /(?:^|\n) {0,3}:::[a-z]/.exec(src);
          return m ? m.index + (m[0].startsWith('\n') ? 1 : 0) : undefined;
        },
        tokenizer(src: string) {
          const open = OPEN.exec(src);
          if (!open || !BLOCKS.has(open[1]!)) return undefined;
          const extent = containerExtent(src, open[0].length);
          if (!extent) return undefined;
          const name = open[1]!;
          const token: ContainerToken = {
            type: 'pagesContainer',
            raw: extent.raw,
            name,
            label: open[2] ?? '',
            attrs: parseAttributes(open[3]),
            body: extent.body,
            tokens: [],
          };
          if (name === 'deflist') {
            const entries: NonNullable<ContainerToken['entries']> = [];
            for (const line of extent.body.split('\n')) {
              if (!line.trim()) continue;
              const def = DEFINITION.exec(line);
              if (def && entries.length) entries.at(-1)!.definitions.push(this.lexer.inlineTokens(def[1]!));
              else if (!def) entries.push({ term: line.trim(), definitions: [] });
            }
            token.entries = entries;
          } else if (name === 'footnotes') {
            const notes: { id: string; text: string }[] = [];
            for (const line of extent.body.split('\n')) {
              const note = FOOTNOTE_LINE.exec(line);
              if (note) notes.push({ id: note[1]!.toLowerCase(), text: note[2]! });
              else if (line.trim() && notes.length) notes.at(-1)!.text += ` ${line.trim()}`;
            }
            token.notes = notes.map((n) => ({ id: n.id, tokens: this.lexer.inlineTokens(n.text) }));
          } else if (name === 'toc') {
            // A placeholder; its body, if any, is ignored.
          } else if (name !== 'diagram') {
            token.tokens = this.lexer.blockTokens(extent.body, []);
          }
          if (name === 'figure' && token.label.trim()) token.caption = this.lexer.inlineTokens(token.label.trim());
          return token;
        },
        renderer(generic) {
          const token = generic as ContainerToken;
          const inner = () => this.parser.parse(token.tokens);

          if (token.name === 'callout') {
            const raw = token.attrs.severity as Severity | undefined;
            const severity: Severity = raw && SEVERITIES.includes(raw) ? raw : 'note';
            const title = token.label.trim() ? escapeHtml(token.label.trim()) : SEVERITY_TITLE[severity];
            return (
              `<aside class="pages-callout" data-severity="${severity}" role="note">` +
              `<p class="pages-callout-title">${title}</p>${inner()}</aside>\n`
            );
          }

          if (token.name === 'details') {
            const open = token.attrs.open === 'true' ? ' open' : '';
            const summary = token.label.trim() ? escapeHtml(token.label.trim()) : 'Details';
            return `<details class="pages-details"${open}><summary>${summary}</summary>${inner()}</details>\n`;
          }

          if (token.name === 'figure') {
            const width = pick(MENUS.figureWidth, token.attrs.width);
            const align = pick(MENUS.figureAlign, token.attrs.align);
            const caption = token.caption ? `<figcaption>${this.parser.parseInline(token.caption)}</figcaption>` : '';
            return `<figure class="pages-figure" data-width="${width}" data-align="${align}">${inner()}${caption}</figure>\n`;
          }

          if (token.name === 'aside') {
            const align = pick(MENUS.asideAlign, token.attrs.align);
            const title = token.label.trim()
              ? `<p class="pages-aside-title">${escapeHtml(token.label.trim())}</p>`
              : '';
            return `<aside class="pages-aside" data-align="${align}">${title}${inner()}</aside>\n`;
          }

          // A grid whose cells are its :::column children. Content outside a
          // column becomes a cell of its own rather than vanishing.
          if (token.name === 'columns') {
            const count = pick(MENUS.columnsCount, token.attrs.count);
            const ratio = pick(MENUS.columnsRatio, token.attrs.ratio);
            return `<div class="pages-columns" data-count="${count}" data-ratio="${ratio}">${inner()}</div>\n`;
          }

          if (token.name === 'column') return `<div class="pages-column">${inner()}</div>\n`;

          if (token.name === 'card') {
            const emphasis = pick(MENUS.cardEmphasis, token.attrs.emphasis);
            const title = token.label.trim()
              ? `<p class="pages-card-title">${escapeHtml(token.label.trim())}</p>`
              : '';
            return `<section class="pages-card" data-emphasis="${emphasis}">${title}${inner()}</section>\n`;
          }

          if (token.name === 'deflist') {
            const items = (token.entries ?? [])
              .map(
                (e) =>
                  `<dt>${escapeHtml(e.term)}</dt>` +
                  e.definitions.map((d) => `<dd>${this.parser.parseInline(d)}</dd>`).join(''),
              )
              .join('');
            return `<dl class="pages-deflist">${items}</dl>\n`;
          }

          // Numbered, ordered and linked back to their refs after sanitising,
          // when the whole page is in hand (`numberFootnotes`).
          if (token.name === 'footnotes') {
            const items = (token.notes ?? [])
              .map((n) => `<li id="pages-fn-${n.id}" data-fn="${n.id}">${this.parser.parseInline(n.tokens)}</li>`)
              .join('');
            return `<section class="pages-footnotes" aria-label="Footnotes"><ol>${items}</ol></section>\n`;
          }

          // Filled from the page's headings after sanitising (`fillInlineToc`).
          if (token.name === 'toc') {
            const depth = pick(MENUS.tocDepth, token.attrs.depth);
            return `<nav class="pages-toc-inline" data-depth="${depth}" aria-label="Contents"></nav>\n`;
          }

          // diagram. The body is diagram SOURCE and never markdown or HTML, so
          // it is escaped into a code block. DRAWING IS OFF: rendering it let a
          // page's author make every reader's browser fetch a URL of their
          // choosing, private pages included (cloistr-pr on MR 78). Until that
          // is closed a diagram is its source, and nothing marks it for drawing.
          const type = token.attrs.type ?? '';
          const source = escapeHtml(token.body.replace(/\n+$/, ''));
          const language = DIAGRAM_TYPES.has(type) ? ` class="language-${type}"` : '';
          return `<pre class="pages-code"><code${language}>${source}</code></pre>\n`;
        },
      },
      {
        name: 'pagesInline',
        level: 'inline',
        start(src: string) {
          const m = INLINE_START.exec(src);
          return m ? m.index : undefined;
        },
        tokenizer(src: string) {
          const m = INLINE.exec(src);
          if (!m) return undefined;
          const token: InlineToken = {
            type: 'pagesInline',
            raw: m[0],
            name: m[1]!,
            label: m[2]!,
            attrs: parseAttributes(m[3]),
            tokens: [],
          };
          // Only a highlight carries formatting inside it. A key is a key, and
          // a pageref's label is the link text, kept plain like :ref's.
          if (token.name === 'mark' || token.name === 'abbr') {
            token.tokens = this.lexer.inlineTokens(token.label);
          }
          return token;
        },
        renderer(generic) {
          const token = generic as InlineToken;
          const label = escapeHtml(token.label);

          if (token.name === 'kbd') return `<kbd>${label}</kbd>`;

          // The label is the note's id; the number shown is assigned later,
          // in reading order (`numberFootnotes`).
          if (token.name === 'footnote-ref') {
            const id = token.label.toLowerCase();
            if (!FOOTNOTE_ID.test(id)) return escapeHtml(token.raw);
            return `<sup class="pages-fnref" data-fn="${id}"><a href="#pages-fn-${id}">${id}</a></sup>`;
          }
          if (token.name === 'mark') return `<mark>${this.parser.parseInline(token.tokens)}</mark>`;

          if (token.name === 'abbr') {
            const inner = this.parser.parseInline(token.tokens);
            const title = token.attrs.title?.trim();
            // No expansion, no element: an abbr without one says nothing a
            // reader can get at.
            return title ? `<abbr title="${escapeHtml(title)}">${inner}</abbr>` : inner;
          }

          // pageref: a page by coordinate or naddr, to its local route, by
          // the same rule as :ref. Anything else leaves the label as text.
          const route = token.attrs.page ? pageRoute(token.attrs.page) : null;
          if (!route) return label;
          return `<a class="pages-pageref" href="${escapeHtml(route)}" data-page-link="true">${label}</a>`;
        },
      },
      {
        name: 'pagesRef',
        level: 'inline',
        start(src: string) {
          const at = src.indexOf(':ref[');
          return at < 0 ? undefined : at;
        },
        tokenizer(src: string) {
          const m = REF.exec(src);
          if (!m) return undefined;
          const token: RefToken = {
            type: 'pagesRef',
            raw: m[0],
            label: m[1]!,
            attrs: parseAttributes(m[2]),
          };
          return token;
        },
        renderer(generic) {
          return renderRef(generic as RefToken, urls);
        },
      },
    ],
  };
}
